// A board (`/work/b/{id}`; spec 06 "Views: list, board, table, calendar, timeline, gallery; saved
// views with filters, sort, group-by; per-person overrides do not save unless Save view"; design
// spec §6.2 tabs + toolbar; Work OS board.tsx, toolbar.tsx, bulk-bar.tsx):
// - The saved views are tabs (`?view=`); the server evaluates a saved view (`boards/{id}/tasks
//   ?view=`, its filter, sort and group). Changing Filter, Sort or Group makes a draft that only
//   this person sees: it is evaluated here over the board's tasks with the same engine, and the
//   draft bar offers Save view (everyone), Save as new view, or Discard.
// - Board: drag a card to another column to set its status (or priority, or the grouped field);
//   a blocked task refuses to start and the toast says why. Inline add in every column and group.
// - List and table select tasks for the bulk bar (status, assign, move, delete; `tasks.bulk`
//   answers per task, and the toast names the ones that did not change).
// - Calendar (by due date; drag a task to another day), timeline (start → due), gallery.
// WORK-OS (the founder's "not a work OS with tasks and projects"): the header carries the
// board's people, its done/total progress and its home channel (`boards.summary`); the six
// view kinds are the tabs (Asana's Board · List · Table · Calendar · Timeline · Gallery), each
// opening the saved view of that kind or, when there is none, showing the active view as that
// kind for this person (`?kind=`, "Save as new view" keeps it); the saved views sit in a menu.
// Cards count their subtasks, and the timeline draws the dependencies.
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AvatarStack,
  BoardView,
  type BoardViewColumn,
  Button,
  CalendarView,
  Checkbox,
  cn,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DueLabel,
  EmptyState,
  FilterChip,
  FilterChips,
  GalleryView,
  Kbd,
  ListView,
  LocationHeader,
  NativeSelect,
  NativeSelectOption,
  PriorityMark,
  Progress,
  SearchField,
  Skeleton,
  Switch,
  TableView,
  type TableViewColumn,
  TimelineView,
  Toolbar,
  ToolbarButton,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  toast,
  ViewTabs,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  ArrowUpDownIcon,
  BookmarkIcon,
  BookOpenTextIcon,
  CalendarDaysIcon,
  ChartGanttIcon,
  ChevronDownIcon,
  FolderInputIcon,
  GroupIcon,
  HashIcon,
  LayoutGridIcon,
  ListFilterIcon,
  ListIcon,
  MessageSquareIcon,
  PaperclipIcon,
  PencilIcon,
  PinIcon,
  PinOffIcon,
  PlusIcon,
  RotateCcwIcon,
  Settings2Icon,
  SquareKanbanIcon,
  Table2Icon,
  Trash2Icon,
  UploadIcon,
  UserIcon,
  UserPlusIcon,
  XIcon,
} from "lucide-react";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import type { Api } from "../../api/client";
import { keepFocus } from "../../focus/focus";
import { clock, daysCovered, daysOf, windowOf } from "../calendar/calendar-model";
import { occurrencesQuery } from "../calendar/calendar-queries";
import { useBoardEventsOverlay } from "../calendar/calendar-toggles";
import { type CompanyAccess, isManager, isMember, isOperatorOnly } from "../company";
import { myMembershipId } from "../home/home-queries";
import { LoadError } from "../screen-parts";
import { useSession } from "../use-feature";
import { useFavorites } from "./arrangement";
import { BoardColorButton, LeadPicker } from "./board-controls";
import { BoardNumbersTab } from "./board-numbers";
import {
  canEditBoardSettings,
  defaultTab,
  openedSearch,
  openingKind,
  openingView,
} from "./board-opening";
import { BoardSettingsSheet } from "./board-settings/board-settings-sheet";
import { useBoardWrites } from "./board-writes";
import {
  cardFieldName,
  cardFieldTexts,
  dateFieldChoices,
  dayWrite,
  effectiveCardFields,
  taskDay,
  viewDateField,
} from "./card-fields";
import { useBoardCovers } from "./files";
import { AssigneeMenu, StatusMenu, useTaskWrites } from "./inline-editors";
import { taskIdOf, useCreateKey, useListKeys } from "./list-keys";
import {
  dueInstant,
  isDone,
  localDay,
  PRIORITIES,
  priorityKey,
  type SeatPeople,
  statusTone,
  subtaskCounts,
  taskRef,
  toSummary,
  useSeatPeople,
} from "./model";
import { PlaceFiles } from "./place-files";
import { useRecordRecent } from "./recents";
import { BulkTagMenu, TagFilterGroup, tagFilterLabel, useTags } from "./tags";
import { boardLayoutQuery, usePref } from "./task-page/queries";
import { useTaskRowActions } from "./task-row-actions";
import { BoardTimeTotal } from "./task-time";
import {
  applyView,
  type Board,
  type FieldDef,
  inValues,
  sameView,
  type TaskCard,
  type View,
  type ViewContext,
  validGroupBy,
  withIn,
} from "./view-engine";
import {
  BoardMenu,
  type NewTaskDefaults,
  NewTaskDialog,
  NewViewDialog,
  RenameDialog,
  VIEW_KINDS,
} from "./work-dialogs";
import { forgetBoard, rememberBoard } from "./work-landing";
import {
  boardAllTasksQuery,
  boardQuery,
  boardSummariesQuery,
  boardsQuery,
  boardTasksQuery,
  fieldsQuery,
  invalidateWork,
  spacesQuery,
  viewsQuery,
  workKeys,
} from "./work-queries";
import { BODY, FavoriteStar } from "./work-screens";

type Settings = Pick<View, "filter" | "sort" | "group_by">;
type Group = { key: string | null; label: string; tasks: TaskCard[] };
export type ViewKind = View["kind"];
type OpensOn = {
  mine: ViewKind | null;
  board: ViewKind | null;
  /** `done` runs once the prefs save answered (the error when it failed). */
  setMine: (kind: ViewKind | null, done: (error?: unknown) => void) => void;
  /**
   * Absent on an archived board and for whoever may not edit the board. `done` runs once the
   * save answered (the error when it failed).
   */
  setBoard: ((kind: ViewKind | null, done: (error?: unknown) => void) => void) | undefined;
};

const KIND_ICON = {
  board: SquareKanbanIcon,
  list: ListIcon,
  table: Table2Icon,
  calendar: CalendarDaysIcon,
  timeline: ChartGanttIcon,
  gallery: LayoutGridIcon,
} as const;

const kindLabel = (k: ViewKind) => VIEW_KINDS.find((x) => x.value === k)?.label ?? k;

/** A board with no saved view reads as a status board. */
const FALLBACK: Settings = { filter: {}, sort: [], group_by: "status" };
/** What a board card and a gallery card already show without card fields. */
const ON_BOARD_CARD = new Set(["status", "priority", "assignee", "due_at"]);
const ON_GALLERY_CARD = new Set(["status", "assignee", "due_at"]);
const NONE = "~none";

const SORT_FIELDS = [
  { value: "number", label: "Created" },
  { value: "due_at", label: "Due date" },
  { value: "start_at", label: "Start date" },
  { value: "priority", label: "Priority" },
  { value: "status", label: "Status" },
  { value: "title", label: "Title" },
] as const;

export function BoardScreen({
  api,
  company,
  boardId,
  viewId,
  kind,
  taskId,
  onOpenTask,
  onView,
  onKind,
  onOpened,
}: {
  api: Api;
  company: CompanyAccess;
  boardId: string;
  /** BOARD-DEFAULT-VIEW: names what the board opened on in the URL (replacing the entry). */
  onOpened: (search: { view: string } | { kind: ViewKind }) => void;
  viewId: string | undefined;
  /** WORK-OS: the active view shown as another kind, for this person (`?kind=`). */
  kind: ViewKind | undefined;
  taskId: string | undefined;
  onOpenTask: (id: string) => void;
  onView: (id: string) => void;
  onKind: (kind: ViewKind | null) => void;
}) {
  const co = company.id;
  const board = useQuery(boardQuery(api, co, boardId));
  const views = useQuery(viewsQuery(api, co, boardId));
  const spaces = useQuery(spacesQuery(api, co));
  const summaries = useQuery(boardSummariesQuery(api, co));
  const people = useSeatPeople(api, co);
  const favorites = useFavorites(co);
  useRecordRecent(api, co, "board", board.data ? boardId : null);
  const boardWrites = useBoardWrites(api, company);
  const queryClient = useQueryClient();
  const { me } = useSession();
  // WORKFORCE-BOARDVIEW: the tab each board opens on for this person.
  const [viewPref, setViewPref, viewPrefRead] = usePref(api, co, "board_view", {});
  // wp/ACTIONS: the board settings sheet (statuses), from the header.
  const [settingsOpen, setSettingsOpen] = useState(false);
  // T6-BOARD-NOTE: the tab the sheet opens on ("how": How it works, from its header icon).
  const [settingsTab, setSettingsTab] = useState<string | undefined>(undefined);
  const openSettings = (tab?: string) => {
    setSettingsTab(tab);
    setSettingsOpen(true);
  };
  // The board you opened last is where Work opens next; one that is gone is forgotten.
  useEffect(() => {
    if (board.data) rememberBoard(company.slug, boardId);
    else if (board.isError) forgetBoard(company.slug, boardId);
  }, [board.data, board.isError, boardId, company.slug]);
  // BOARD-DEFAULT-VIEW: opened without `?view=` / `?kind=`, the URL takes what it opened on
  // (a replace, no new history entry), so a reload or a shared link keeps it.
  const opened =
    board.data && views.data && viewPrefRead
      ? openedSearch(
          openingView(
            [...views.data.data].sort((x, y) => x.position - y.position),
            viewId,
            kind,
            openingKind(board.data.id, board.data.settings.default_kind, viewPref.kinds),
          ),
          viewId,
          kind,
        )
      : null;
  const openedKey = opened ? JSON.stringify(opened) : null;
  const lastOpened = useRef<string | null>(null);
  useEffect(() => {
    // Once the URL names a view, a later bare link (the sidebar row again) writes it anew.
    if (!openedKey) lastOpened.current = null;
    if (!openedKey || lastOpened.current === openedKey) return;
    lastOpened.current = openedKey;
    onOpened(JSON.parse(openedKey) as { view: string } | { kind: ViewKind });
  }, [openedKey, onOpened]);

  if (board.isPending || views.isPending || !viewPrefRead)
    return (
      <div role="status" aria-label="Loading the board" className="flex flex-col gap-3 px-6 py-4">
        <Skeleton className="h-9 w-1/3" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  if (board.isError)
    return (
      <div className="px-6 py-4">
        <EmptyState
          icon={SquareKanbanIcon}
          title="This board is not available"
          titleAs="h1"
          description="It was archived, or it sits in a space you are not a member of."
          actions={
            <Button
              variant="secondary"
              render={
                <Link to="/c/$slug/work/my" params={{ slug: company.slug }} search={keepFocus} />
              }
            >
              My tasks
            </Button>
          }
        />
      </div>
    );
  if (views.isError)
    return (
      <div className="px-6 py-4">
        <LoadError what="The board's views" error={views.error} />
      </div>
    );

  const b = board.data;
  const list = [...views.data.data].sort((x, y) => x.position - y.position);
  // WORKFORCE-BOARDVIEW: without `?view=` or `?kind=`, the person's default tab, else the board's.
  const mine = viewPref.kinds?.[b.id] ?? null;
  const opening = openingView(
    list,
    viewId,
    kind,
    openingKind(b.id, b.settings.default_kind, viewPref.kinds),
  );
  const active = opening.active;
  const setMine = (k: ViewKind | null, done: (error?: unknown) => void) => {
    const { [b.id]: _drop, ...rest } = viewPref.kinds ?? {};
    setViewPref(
      { kinds: k ? { ...rest, [b.id]: k } : rest },
      { onSuccess: () => done(), onError: (e) => done(e ?? new Error("not saved")) },
    );
  };
  const space = spaces.data?.data.find((s) => s.id === b.space_id);
  // The board's default is a board settings edit (board#editor), never a space viewer's.
  // The API says (`can_edit`); the client's own reading only where it does not (older twin).
  const editsBoard =
    !isOperatorOnly(company) &&
    (b.can_edit ??
      canEditBoardSettings(
        {
          manager: isManager(company),
          member: isMember(company),
          membershipId: myMembershipId(me, company),
        },
        space,
      ));
  const summary = summaries.data?.find((s) => s.board_id === b.id);
  const members = (summary?.assignee_seat_ids ?? []).map((id) => people.of(id));
  const fav = { kind: "board" as const, id: b.id };
  return (
    <div data-screen="board" data-board={b.id} className="flex min-h-full min-w-0 flex-col">
      <LocationHeader
        crumbs={
          space
            ? [
                {
                  label: space.name,
                  render: (
                    <Link
                      to="/c/$slug/work/s/$spaceId"
                      params={{ slug: company.slug, spaceId: space.id }}
                      // BOARD-DEFAULT-VIEW: the crumb shows the space's overview (opening the
                      // space would land on its default board, often this one).
                      search={(prev: Record<string, unknown>) => ({
                        ...keepFocus(prev),
                        view: "overview" as const,
                      })}
                    />
                  ),
                },
                { label: b.name },
              ]
            : [{ label: "Board" }, { label: b.name }]
        }
        // BOARD-ORDER: the mark opens the colour swatches (a live board).
        icon={() => (
          <BoardColorButton
            board={b}
            size="md"
            onPick={b.archived_at ? undefined : (color) => boardWrites.color(b, color)}
          />
        )}
        title={b.name}
        badge={
          <>
            <FavoriteStar company={company} favorite={fav} name={b.name} />
            {/* BOARD-ORDER: the board's lead, picked here (the people who hold seats). */}
            <LeadPicker
              people={people}
              seatId={b.lead_seat_id ?? summary?.lead_seat_id ?? null}
              boardName={b.name}
              className="ml-1"
              onPick={b.archived_at ? undefined : (seat) => boardWrites.lead(b, seat)}
            />
            {/* WORK-OS: the board's people, its progress and its home channel beside the name. */}
            {summary ? (
              <span
                data-slot="board-vitals"
                className="ml-2 flex min-w-0 items-center gap-3 text-xs text-muted-foreground max-md:hidden"
              >
                {members.length ? (
                  <AvatarStack
                    size="sm"
                    aria-label={`People on this board: ${members.map((p) => p.name).join(", ")}`}
                    items={members.map((p) => ({
                      name: p.name,
                      kind: p.de ? "agent" : "person",
                      title: p.full,
                    }))}
                  />
                ) : null}
                <span
                  className="flex items-center gap-2"
                  title={`${summary.done} of ${summary.total} tasks done`}
                >
                  <Progress
                    value={summary.total ? Math.round((summary.done / summary.total) * 100) : 0}
                    aria-label={`${summary.done} of ${summary.total} tasks done`}
                    className="w-20 flex-nowrap"
                  />
                  <span className="tabular-nums">
                    {summary.done}/{summary.total}
                  </span>
                  {summary.overdue ? (
                    <span className="font-medium text-danger-text tabular-nums">
                      {summary.overdue} overdue
                    </span>
                  ) : null}
                </span>
                {/* WOS-BUNDLE: the time booked on the board's tasks. */}
                <BoardTimeTotal api={api} company={company} boardId={b.id} />
              </span>
            ) : null}
          </>
        }
        actions={
          <>
            {b.home_channel_id ? (
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Open the home channel"
                      nativeButton={false}
                      render={
                        <Link
                          to="/c/$slug/chat/$channelId"
                          params={{ slug: company.slug, channelId: b.home_channel_id }}
                          search={keepFocus}
                        />
                      }
                    />
                  }
                >
                  <MessageSquareIcon aria-hidden="true" />
                </TooltipTrigger>
                <TooltipContent>Home channel: digests and task cards</TooltipContent>
              </Tooltip>
            ) : null}
            {/* M2-08: the import wizard's Tasks kind, with this board chosen (managers). */}
            {isManager(company) && b.archived_at === null ? (
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Import tasks"
                      nativeButton={false}
                      render={
                        <Link
                          to="/c/$slug/kpis/import"
                          params={{ slug: company.slug }}
                          search={{ kind: "tasks", board: b.id }}
                        />
                      }
                    />
                  }
                >
                  <UploadIcon aria-hidden="true" />
                </TooltipTrigger>
                <TooltipContent>Import tasks (CSV, ClickUp)</TooltipContent>
              </Tooltip>
            ) : null}
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="How this board works"
                    onClick={() => openSettings("how")}
                  />
                }
              >
                <BookOpenTextIcon aria-hidden="true" />
              </TooltipTrigger>
              <TooltipContent>How it works: owner, note and rules</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Board settings"
                    onClick={() => openSettings()}
                  />
                }
              >
                <Settings2Icon aria-hidden="true" />
              </TooltipTrigger>
              <TooltipContent>Board settings: statuses, fields and views</TooltipContent>
            </Tooltip>
            <BoardMenu
              api={api}
              company={company}
              board={b}
              star={{ on: favorites.has(fav), toggle: () => favorites.toggle(fav, b.name) }}
              onSettings={() => openSettings()}
              trigger={<Button variant="ghost" size="icon-sm" aria-label="Board actions" />}
            />
            <BoardSettingsSheet
              key={settingsTab ?? "general"}
              board={b}
              can={{ edit: editsBoard, manage: editsBoard }}
              api={api}
              co={co}
              company={company}
              tab={settingsTab}
              open={settingsOpen}
              onOpenChange={setSettingsOpen}
              saveStatuses={(status_set) =>
                api.client.boards
                  .update({ params: { co, id: b.id }, ifMatch: b.version, body: { status_set } })
                  .then(async () => {
                    toast.add({ title: "Statuses saved", type: "success" });
                    await invalidateWork(queryClient, co);
                  })
              }
            />
          </>
        }
      />
      <div className={BODY}>
        {/* Remounted per view: a draft belongs to the view it was made on. */}
        <BoardBody
          key={active?.id ?? "none"}
          api={api}
          company={company}
          board={b}
          views={list}
          active={active}
          kindOverride={opening.kind}
          taskId={taskId}
          onOpenTask={onOpenTask}
          onView={onView}
          onKind={onKind}
          editsViews={!b.archived_at && editsBoard}
          opensOn={{
            mine,
            board: b.settings.default_kind ?? null,
            setMine,
            // Only for whoever may edit the board's settings (`editsBoard`, board-opening.ts).
            setBoard:
              b.archived_at || !editsBoard
                ? undefined
                : (k, done) =>
                    boardWrites.defaultKind(b, k, {
                      onSuccess: () => done(),
                      onError: (e) => done(e ?? new Error("not saved")),
                    }),
          }}
        />
      </div>
    </div>
  );
}

function BoardBody({
  api,
  company,
  board,
  views,
  active,
  kindOverride,
  taskId,
  onOpenTask,
  onView,
  onKind,
  editsViews,
  opensOn,
}: {
  api: Api;
  company: CompanyAccess;
  board: Board;
  views: View[];
  active: View | null;
  kindOverride: ViewKind | undefined;
  taskId: string | undefined;
  onOpenTask: (id: string) => void;
  onView: (id: string) => void;
  onKind: (kind: ViewKind | null) => void;
  /** wp/ACTIONS: rename / delete saved views (board writers: the API's `views.*` gate). */
  editsViews: boolean;
  /** WORKFORCE-BOARDVIEW: the tab the board opens on (mine, the board's) and their setters. */
  opensOn: OpensOn;
}) {
  const co = company.id;
  const queryClient = useQueryClient();
  const people = useSeatPeople(api, co);
  const fields = useQuery(fieldsQuery(api, co, board.id));
  const boards = useQuery(boardsQuery(api, co));
  const writes = useTaskWrites(api, company);
  const tags = useTags(api, co);
  // WOS-BUNDLE: a list row's timer and "…" menu (Open, Duplicate…).
  const rowActions = useTaskRowActions(api, company, { onOpenTask });
  const saved: Settings = active ?? FALLBACK;
  const [draft, setDraft] = useState<Settings | null>(null);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const [newView, setNewView] = useState<{ open: boolean; asCopy: boolean }>({
    open: false,
    asCopy: false,
  });
  const [adding, setAdding] = useState<NewTaskDefaults | null>(null);
  // TASK-FILES / T8-BOARD-NUMBERS: a side tab (Files, Numbers) in place of the view kinds.
  const [sideTab, setSideTab] = useState<"files" | "numbers" | null>(null);
  const filesTab = sideTab !== null;
  // Keyboard first: `c` creates, `j` / `k` walk the rows (or cards) and Enter opens one.
  const body = useRef<HTMLDivElement>(null);
  useCreateKey(() => setAdding({}));
  const keys = useMemo(
    () => ({
      onOpen: (row: HTMLElement) => {
        const id = taskIdOf(row);
        if (id) onOpenTask(id);
      },
    }),
    [onOpenTask],
  );
  useListKeys(body, keys);
  // WORK-OS: a kind tab without a saved view shows the active view's tasks as that kind.
  const kind: ViewKind = kindOverride ?? active?.kind ?? "board";
  const settings = draft ?? saved;
  const dirty = draft !== null && !sameView(draft, saved);
  const fieldList = fields.data?.data ?? [];
  const ctx: ViewContext = useMemo(
    () => ({
      statuses: board.status_set,
      fields: fieldList,
      seatLabel: (id: string) => people.of(id).label,
    }),
    [board.status_set, fieldList, people],
  );

  // Saved: the server's answer. Draft: every task of the board, through the same engine.
  const server = useQuery({
    ...boardTasksQuery(api, co, board.id, active?.id ?? null),
    enabled: !dirty,
  });
  const all = useInfiniteQuery({ ...boardAllTasksQuery(api, co, board.id), enabled: dirty });
  const groups: Group[] | undefined = dirty
    ? all.data
      ? applyView(
          all.data.pages.flatMap((p) => p.data),
          settings,
          ctx,
        )
      : undefined
    : server.data
      ? (server.data.groups?.map((g) => ({
          key: g.key,
          // The server labels an assignee group by the seat; people think in people.
          label: settings.group_by === "assignee" && g.key ? people.of(g.key).label : g.label,
          tasks: g.data,
        })) ?? [{ key: null, label: "All tasks", tasks: server.data.data }])
      : undefined;
  const loading = dirty ? all.isPending : server.isPending;
  const error = dirty ? all.error : server.error;

  const q = search.trim().toLowerCase();
  const shown = (groups ?? []).map((g) => ({
    ...g,
    tasks: q
      ? g.tasks.filter(
          (t) => t.title.toLowerCase().includes(q) || taskRef(t) === q || `${t.number}` === q,
        )
      : g.tasks,
  }));
  const flat = [...new Map(shown.flatMap((g) => g.tasks).map((t) => [t.id, t])).values()];
  const today = localDay(new Date());
  const chips = fieldList
    .filter((f) => f.type === "select")
    .map((f) => ({ id: f.id, choices: f.options.choices ?? [] }));
  // WORK-OS: subtask counts on the cards, from the subtasks in the same view. On the board and
  // in the gallery a subtask folds into its parent's card (its counter) when the parent is shown
  // too (ClickUp's default); rows and bars keep every task, a subtask naming its parent.
  const subtasks = subtaskCounts(flat, board);
  const byId = new Map(flat.map((t) => [t.id, t] as const));
  const folded = kind === "board" || kind === "gallery";
  const visible = (t: TaskCard) => !(folded && t.parent_task_id && byId.has(t.parent_task_id));
  // TASK-FILES: cover images on the board's cards (up to four each); T4-VIEWS-MEDIA: the gallery
  // and the calendar chips show the first one.
  const covers = useBoardCovers(
    api,
    co,
    board.id,
    kind === "board" || kind === "gallery" || kind === "calendar",
  );
  // T4-VIEWS-MEDIA: the view's card fields (else the fields marked "On cards", else the older
  // single-choice chips) and the date its calendar and timeline run on.
  const layout = useQuery(boardLayoutQuery(api, co, board.id));
  const cardRefs = effectiveCardFields(active, fieldList, layout.data?.fields);
  const dateField = viewDateField(active, fieldList);
  const cardCtx = { fields: fieldList, board, seat: (id: string) => people.of(id).label };
  const extraTexts = (t: TaskCard, max: number, shown: ReadonlySet<string>) =>
    cardRefs
      ? cardFieldTexts(
          t,
          cardRefs.filter((r) => !shown.has(r)),
          cardCtx,
          max,
        )
      : [];
  const summary = (t: TaskCard) => {
    const parent = t.parent_task_id ? byId.get(t.parent_task_id) : undefined;
    const s = toSummary(t, {
      board,
      people,
      today,
      chips: cardRefs ? [] : chips,
      subtasks,
      tags: tags.byId,
      ...(parent ? { location: `↳ ${parent.title}` } : {}),
    });
    const labels = [
      ...(s.labels ?? []),
      ...extraTexts(t, 3, ON_BOARD_CARD).map((label) => ({ label, tone: "neutral" as const })),
    ];
    const c = kind === "board" ? covers.get(t.id) : undefined;
    return {
      ...s,
      ...(labels.length ? { labels } : {}),
      ...(c?.length ? { covers: c } : {}),
    };
  };

  const refresh = () => invalidateWork(queryClient, co);
  // WORK-PERF: every task write shows at once (task-writes.ts); a refusal undoes it.
  const setStatus = {
    mutate: (v: { id: string; status: string }) => writes.setStatus(v.id, v.status),
  };
  const update = {
    mutate: (v: { task: TaskCard; body: { priority?: number; due_at?: string | null } }) =>
      writes.update(v.task.id, v.body),
  };
  const setField = {
    mutate: (v: { id: string; field: string; value: unknown }) =>
      writes.setField(v.id, v.field, v.value),
  };
  const create = {
    mutate: (v: { title: string; defaults: NewTaskDefaults & { field?: [string, string] } }) =>
      writes.create({
        board_id: board.id,
        title: v.title,
        ...(v.defaults.status ? { status: v.defaults.status } : {}),
        ...(v.defaults.priority !== undefined ? { priority: v.defaults.priority } : {}),
        ...(v.defaults.due ? { due_at: dueInstant(v.defaults.due) } : {}),
        ...(v.defaults.field ? { fields: { [v.defaults.field[0]]: v.defaults.field[1] } } : {}),
      }),
  };
  const saveView = useMutation({
    mutationFn: (s: Settings) =>
      api.client.views.update({
        params: { co, id: board.id, sub_id: active?.id ?? "" },
        body: { filter: s.filter, sort: s.sort, group_by: s.group_by },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: workKeys.views(co, board.id) });
      await refresh();
      setDraft(null);
      toast.add({ title: `${active?.name ?? "View"} saved for everyone`, type: "success" });
    },
  });
  // T4-VIEWS-MEDIA: the calendar's date picker saves the view's date for everyone.
  const setViewDate = useMutation({
    mutationFn: (ref: string) =>
      api.client.views.update({
        params: { co, id: board.id, sub_id: active?.id ?? "" },
        body: { date_field: ref },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: workKeys.views(co, board.id) }),
  });
  // wp/ACTIONS: a saved view's Rename and Delete (hover icons on its row in the Views menu).
  const [viewsOpen, setViewsOpen] = useState(false);
  const [viewEdit, setViewEdit] = useState<{ kind: "rename" | "delete"; view: View } | null>(null);
  const renameView = (v: View, name: string) =>
    api.client.views
      .update({ params: { co, id: board.id, sub_id: v.id }, body: { name } })
      .then(async () => {
        toast.add({ title: `Renamed to ${name}`, type: "success" });
        await queryClient.invalidateQueries({ queryKey: workKeys.views(co, board.id) });
      });
  const deleteView = useMutation({
    mutationFn: (v: View) =>
      api.client.views.delete({ params: { co, id: board.id, sub_id: v.id } }),
    onSuccess: async (_r, v) => {
      toast.add({ title: `${v.name} deleted`, type: "success" });
      if (v.id === active?.id) {
        const next = views.find((x) => x.id !== v.id);
        if (next) onView(next.id);
      }
      await queryClient.invalidateQueries({ queryKey: workKeys.views(co, board.id) });
    },
  });
  const createView = useMutation({
    mutationFn: (v: { name: string; kind: View["kind"]; settings: Settings }) =>
      api.client.views.create({
        params: { co, id: board.id },
        body: {
          name: v.name,
          kind: v.kind,
          filter: v.settings.filter,
          sort: v.settings.sort,
          group_by: v.settings.group_by,
          position: views.length,
        },
      }),
    onSuccess: async (view) => {
      await queryClient.invalidateQueries({ queryKey: workKeys.views(co, board.id) });
      setNewView({ open: false, asCopy: false });
      toast.add({ title: `${view.name} added as a view`, type: "success" });
      onView(view.id);
    },
  });

  /** Defaults for a task added in a group: its status, priority or field value. */
  const defaultsFor = (groupKey: string | null): NewTaskDefaults & { field?: [string, string] } => {
    const by = settings.group_by;
    if (by === "status" && groupKey) return { status: groupKey };
    if (by === "priority" && groupKey) return { priority: Number(groupKey) };
    if (by?.startsWith("field:") && groupKey) return { field: [by.slice(6), groupKey] };
    return {};
  };
  const moveTo = (id: string, columnId: string) => {
    const task = flat.find((t) => t.id === id);
    const key = columnId === NONE ? null : columnId;
    const by = settings.group_by;
    if (!task) return;
    if (by === "status" && key && key !== task.status) setStatus.mutate({ id, status: key });
    else if (by === "priority" && key !== null && Number(key) !== task.priority)
      update.mutate({ task, body: { priority: Number(key) } });
    else if (by?.startsWith("field:")) setField.mutate({ id, field: by.slice(6), value: key });
  };
  const draggable =
    settings.group_by === "status" ||
    settings.group_by === "priority" ||
    Boolean(settings.group_by?.startsWith("field:"));
  const change = (next: Partial<Settings>) => setDraft({ ...settings, ...next });

  // WORK-OS: the six kinds are the tabs (Board · List · Table · Calendar · Timeline · Gallery);
  // a kind with a saved view opens that view (the active one when it is of that kind), a kind
  // without one shows the active view as that kind for this person. The saved views sit in the
  // Views menu, with "+ New view".
  // BOARD-DEFAULT-VIEW (ClickUp): each kind tab has its own menu with "Set as default view" —
  // for everyone when the person may edit the board, else for them — and "Reset to board
  // default" once they have their own. The tab the board opens on carries a "default" mark.
  const marked = defaultTab(opensOn.mine, opensOn.board, views);
  const tabMenu = (k: ViewKind, label: string) => {
    const forEveryone = opensOn.setBoard !== undefined;
    const isDefault = forEveryone ? opensOn.board === k : opensOn.mine === k;
    return (
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label={`${label} view options`}
              title="View options"
              className={cn(
                "opacity-0 group-hover/view-tab:opacity-100 group-focus-within/view-tab:opacity-100 data-popup-open:opacity-100 pointer-coarse:opacity-100",
                k === kind && !filesTab && "opacity-100",
              )}
            />
          }
        >
          <ChevronDownIcon aria-hidden="true" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-52">
          {isDefault ? (
            <DropdownMenuItem
              onClick={() => (forEveryone ? setOpening("board", null) : setOpening("mine", null))}
            >
              <PinOffIcon aria-hidden="true" />
              {forEveryone ? "Clear default view" : "Clear my default"}
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              onClick={() => (forEveryone ? setOpening("board", k) : setOpening("mine", k))}
            >
              <PinIcon aria-hidden="true" />
              {forEveryone ? "Set as default view" : "Set as default for me"}
            </DropdownMenuItem>
          )}
          {forEveryone && opensOn.mine !== k ? (
            <DropdownMenuItem onClick={() => setOpening("mine", k)}>
              <UserIcon aria-hidden="true" />
              Set as default for me
            </DropdownMenuItem>
          ) : null}
          {opensOn.mine ? (
            <DropdownMenuItem onClick={() => setOpening("mine", null)}>
              <RotateCcwIcon aria-hidden="true" />
              Reset to board default
            </DropdownMenuItem>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };
  const tabs = [
    ...VIEW_KINDS.map((k) => {
      const count = views.filter((v) => v.kind === k.value).length;
      const mark = marked?.kind === k.value ? marked.whose : null;
      return {
        value: k.value as string,
        label: k.label,
        icon: KIND_ICON[k.value],
        ...(count > 1 ? { count } : {}),
        ...(mark
          ? {
              marker: (
                <span
                  data-slot="default-mark"
                  title={
                    mark === "mine"
                      ? "Opens here for you"
                      : mark === "board"
                        ? "Opens here for everyone"
                        : "Opens here (the default saved view)"
                  }
                  className="rounded-sm bg-muted px-1 text-[11px] leading-4 font-normal text-muted-foreground"
                >
                  {mark === "mine" ? "my default" : "default"}
                </span>
              ),
            }
          : {}),
        menu: tabMenu(k.value, k.label),
      };
    }),
    // TASK-FILES: every file on the board's tasks.
    { value: "files", label: "Files", icon: PaperclipIcon },
    // T8-BOARD-NUMBERS: counts, tasks per status, done per week and the board's number tiles.
    { value: "numbers", label: "Numbers", icon: HashIcon },
  ];
  const pickKind = (k: string) => {
    const side = k === "files" || k === "numbers" ? k : null;
    setSideTab(side);
    if (side) return;
    const next = k as ViewKind;
    if (next === kind) return;
    // Named in the URL: without it the board's opening tab would apply again.
    if (active?.kind === next) return onView(active.id);
    const view = views.find((v) => v.kind === next);
    if (view) onView(view.id);
    else onKind(next);
  };
  // WORKFORCE-BOARDVIEW: "Set as my default" (me/prefs) and, for whoever may edit the board,
  // "Set as board default" (settings.default_kind; the server refuses a viewer).
  // Either default toasts only once its save answered (an error toast when it failed).
  const setOpening = (who: "mine" | "board", k: ViewKind | null) => {
    const saved = () =>
      toast.add({
        title: k
          ? `${board.name} opens on ${kindLabel(k)} ${who === "mine" ? "for you" : "for everyone"}`
          : who === "mine"
            ? `${board.name} opens on the board's default for you`
            : `${board.name} opens on its default view`,
        type: "success",
      });
    const done = (error?: unknown) =>
      error === undefined
        ? saved()
        : toast.add({
            title:
              who === "mine" ? "Your default was not saved" : "The board default was not saved",
            ...(error instanceof Error ? { description: error.message } : {}),
            type: "error",
          });
    if (who === "mine") opensOn.setMine(k, done);
    else opensOn.setBoard?.(k, done);
  };
  const bodyEmpty = (
    <EmptyState
      variant="compact"
      icon={SquareKanbanIcon}
      title={q || dirty ? "No task matches" : "No tasks yet"}
      description={
        q || dirty
          ? "Clear the search or the filters to see more."
          : "Add the first one with Add task."
      }
    />
  );

  return (
    <>
      <div className="flex items-end gap-2 border-b border-border">
        <ViewTabs
          aria-label="View kinds"
          className="min-w-0"
          value={sideTab ?? kind}
          onValueChange={pickKind}
          tabs={tabs}
        />
        <DropdownMenu open={viewsOpen} onOpenChange={setViewsOpen}>
          <DropdownMenuTrigger
            render={
              <Button
                variant="ghost"
                size="sm"
                className="mb-1.5 ml-auto shrink-0"
                aria-label={`Saved views: ${active?.name ?? "none"}`}
              />
            }
          >
            <BookmarkIcon aria-hidden="true" />
            <span className="max-w-40 truncate">
              {active ? (kindOverride ? `${active.name} as ${kind}` : active.name) : "Views"}
            </span>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-52">
            {views.map((v) => {
              const Icon = KIND_ICON[v.kind];
              const item = (
                <DropdownMenuItem
                  key={v.id}
                  onClick={() => onView(v.id)}
                  aria-current={v.id === active?.id && !kindOverride ? "true" : undefined}
                  className={cn(v.id === active?.id && "font-medium", editsViews && "flex-1")}
                >
                  <Icon aria-hidden="true" />
                  {v.name}
                  {v.is_default ? (
                    <span className="ml-auto text-xs text-muted-foreground">default</span>
                  ) : null}
                </DropdownMenuItem>
              );
              if (!editsViews) return item;
              const edit = (kind: "rename" | "delete") => {
                setViewsOpen(false);
                setViewEdit({ kind, view: v });
              };
              return (
                <div key={v.id} data-slot="view-row" className="group/view flex items-center">
                  {item}
                  <span className="flex shrink-0 items-center opacity-0 group-hover/view:opacity-100 group-focus-within/view:opacity-100 pointer-coarse:opacity-100">
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      aria-label={`Rename the view ${v.name}`}
                      title="Rename"
                      onClick={() => edit("rename")}
                    >
                      <PencilIcon aria-hidden="true" />
                    </Button>
                    {views.length > 1 ? (
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        aria-label={`Delete the view ${v.name}`}
                        title="Delete"
                        className="hover:text-destructive"
                        onClick={() => edit("delete")}
                      >
                        <Trash2Icon aria-hidden="true" />
                      </Button>
                    ) : null}
                  </span>
                </div>
              );
            })}
            {views.length ? <DropdownMenuSeparator /> : null}
            <DropdownMenuItem onClick={() => setNewView({ open: true, asCopy: false })}>
              <PlusIcon aria-hidden="true" />
              New view
            </DropdownMenuItem>
            {kindOverride ? (
              <DropdownMenuItem onClick={() => setNewView({ open: true, asCopy: true })}>
                <BookmarkIcon aria-hidden="true" />
                Save this {kind} as a view
              </DropdownMenuItem>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {viewEdit?.kind === "rename" ? (
        <RenameDialog
          what="view"
          name={viewEdit.view.name}
          open
          onOpenChange={(o) => !o && setViewEdit(null)}
          onSave={(name) => renameView(viewEdit.view, name)}
        />
      ) : null}
      <AlertDialog open={viewEdit?.kind === "delete"} onOpenChange={(o) => !o && setViewEdit(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete the view {viewEdit?.view.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              It goes for everyone on this board. Its tasks stay; only the saved filter, sort and
              grouping go.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                if (viewEdit) deleteView.mutate(viewEdit.view);
                setViewEdit(null);
              }}
            >
              Delete view
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {sideTab === "numbers" ? (
        <BoardNumbersTab api={api} co={co} boardId={board.id} boardName={board.name} />
      ) : null}
      {sideTab === "files" ? (
        <PlaceFiles
          api={api}
          company={company}
          kind="board"
          id={board.id}
          onOpenTask={onOpenTask}
        />
      ) : null}
      <div className={filesTab ? "hidden" : "contents"} data-slot="board-views">
        <Toolbar aria-label="View tools" className="mt-1">
          <SearchField value={search} onChange={(e) => setSearch(e.target.value)} />
          <span className="shrink-0 text-xs text-muted-foreground tabular-nums" aria-live="polite">
            {groups ? `${flat.length} task${flat.length === 1 ? "" : "s"}` : ""}
          </span>
          <FilterMenu
            board={board}
            people={people}
            settings={settings}
            onChange={change}
            className="ml-auto"
            extra={
              <TagFilterGroup
                api={api}
                co={co}
                filter={settings.filter}
                onChange={(filter) => change({ filter })}
              />
            }
          />
          <SortMenu settings={settings} fields={fieldList} onChange={change} />
          <GroupMenu settings={settings} fields={fieldList} onChange={change} />
          <Button
            size="sm"
            className="shrink-0"
            aria-keyshortcuts="c"
            onClick={() => setAdding({})}
          >
            <PlusIcon aria-hidden="true" />
            Add task
            <Kbd aria-hidden="true" className="ml-1 max-md:hidden">
              c
            </Kbd>
          </Button>
        </Toolbar>
        <ActiveFilters
          board={board}
          people={people}
          settings={settings}
          onChange={change}
          tags={tags.byId}
        />
        {dirty ? (
          <section
            aria-label="Unsaved view changes"
            data-slot="view-draft"
            className="mt-2 flex flex-wrap items-center gap-2 rounded-md bg-primary-soft px-3 py-2 text-sm text-primary-text"
          >
            <span className="mr-auto">
              {active
                ? `Only you see these changes to ${active.name}.`
                : "Only you see these view settings."}
            </span>
            {active ? (
              <Button
                size="sm"
                disabled={saveView.isPending}
                onClick={() => saveView.mutate(settings)}
              >
                Save view
              </Button>
            ) : null}
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setNewView({ open: true, asCopy: true })}
            >
              Save as new view
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setDraft(null)}>
              Discard
            </Button>
          </section>
        ) : null}

        <div ref={body} className="mt-3 min-w-0">
          {rowActions.dialog}
          {loading ? (
            <div role="status" aria-label="Loading the tasks" className="flex gap-3">
              <Skeleton className="h-48 flex-1" />
              <Skeleton className="h-48 flex-1" />
              <Skeleton className="h-48 flex-1" />
            </div>
          ) : error ? (
            <LoadError what="The board's tasks" error={error} />
          ) : kind === "board" ? (
            <BoardView
              aria-label={`${board.name} board`}
              columns={shown.map(
                (g): BoardViewColumn => ({
                  id: g.key ?? NONE,
                  title: g.label,
                  tone:
                    settings.group_by === "status"
                      ? statusTone(board.status_set.find((s) => s.key === g.key))
                      : "neutral",
                  tasks: g.tasks.filter(visible).map(summary),
                }),
              )}
              {...(taskId ? { selectedTaskId: taskId } : {})}
              onTaskOpen={onOpenTask}
              {...(draggable ? { onTaskMove: (id: string, col: string) => moveTo(id, col) } : {})}
              onAddTask={(col, title) =>
                create.mutate({ title, defaults: defaultsFor(col === NONE ? null : col) })
              }
              empty={bodyEmpty}
            />
          ) : kind === "list" ? (
            <ListView
              aria-label={`${board.name} list`}
              groups={shown.map((g) => ({
                id: g.key ?? NONE,
                title: g.label,
                tasks: g.tasks.map(summary),
              }))}
              columns={["assignees", "due", "priority"]}
              selectedIds={selected}
              onSelectionChange={setSelected}
              {...(taskId ? { activeTaskId: taskId } : {})}
              onTaskOpen={onOpenTask}
              onToggleDone={(id, done) => {
                const status = done
                  ? board.status_set.find((s) => s.done)?.key
                  : board.status_set.find((s) => !s.done)?.key;
                if (status) setStatus.mutate({ id, status });
              }}
              cells={{
                actions: rowActions.actions,
                status: (s) => {
                  const t = flat.find((x) => x.id === s.id);
                  return t ? (
                    <StatusMenu
                      board={board}
                      status={t.status}
                      onChange={(status) => setStatus.mutate({ id: t.id, status })}
                    />
                  ) : null;
                },
                assignees: (s) => {
                  const t = flat.find((x) => x.id === s.id);
                  return t ? (
                    <AssigneeMenu
                      people={people}
                      seatIds={t.assignee_seat_ids}
                      title={t.title}
                      onToggle={(seat, on) => writes.toggleAssignee(t.id, seat, on)}
                    />
                  ) : null;
                },
              }}
              onAddTask={(g, title) =>
                create.mutate({ title, defaults: defaultsFor(g === NONE ? null : g) })
              }
              empty={bodyEmpty}
            />
          ) : kind === "table" ? (
            <TasksTable
              board={board}
              fields={fieldList}
              people={people}
              rows={flat}
              today={today}
              selected={selected}
              onSelected={setSelected}
              taskId={taskId}
              onOpenTask={onOpenTask}
              onStatus={(id, status) => setStatus.mutate({ id, status })}
              onAssignee={writes.toggleAssignee}
            />
          ) : kind === "calendar" ? (
            <TasksCalendar
              api={api}
              company={company}
              board={board}
              rows={flat}
              today={today}
              dateField={dateField}
              dateLabel={cardFieldName(dateField, fieldList) ?? "Due date"}
              dateChoices={dateFieldChoices(fieldList)}
              {...(editsViews && active && !kindOverride
                ? { onDateField: (ref: string) => setViewDate.mutate(ref) }
                : {})}
              chip={(t) => {
                const thumb = covers.get(t.id)?.[0];
                const texts = cardRefs
                  ? cardFieldTexts(
                      t,
                      cardRefs.filter((r) => r !== "status" && r !== dateField),
                      cardCtx,
                      2,
                    )
                  : [];
                return {
                  ...(thumb ? { thumb } : {}),
                  ...(texts.length ? { fields: texts } : {}),
                };
              }}
              onOpenTask={onOpenTask}
              onMove={(id, day) => {
                const task = flat.find((t) => t.id === id);
                if (!task) return;
                const w = dayWrite(dateField, day);
                if (w.kind === "field") setField.mutate({ id, field: w.field, value: w.value });
                else writes.update(id, w.body);
              }}
              onDayAdd={(day) => {
                const w = dayWrite(dateField, day);
                setAdding(
                  w.kind === "field"
                    ? { fields: { [w.field]: w.value } }
                    : dateField === "start_at"
                      ? { start: day }
                      : { due: day },
                );
              }}
            />
          ) : kind === "timeline" ? (
            <TasksTimeline
              board={board}
              rows={flat}
              today={today}
              dateField={dateField}
              onOpenTask={onOpenTask}
            />
          ) : (
            <GalleryView
              aria-label={`${board.name} cards`}
              items={flat.filter(visible).map((t) => {
                const s = toSummary(t, {
                  board,
                  people,
                  today,
                  chips: cardRefs ? [] : chips,
                  tags: tags.byId,
                });
                const cover = covers.get(t.id)?.[0];
                const texts = extraTexts(t, 4, ON_GALLERY_CARD);
                return {
                  id: t.id,
                  title: `${taskRef(t)} ${t.title}`,
                  ...(cover ? { cover } : {}),
                  ...(texts.length ? { fields: texts } : {}),
                  badges: [
                    { label: s.status?.label ?? t.status, tone: s.status?.tone ?? "neutral" },
                    ...(s.labels ?? []),
                  ],
                  meta: (
                    <span className="flex items-center gap-2">
                      {s.due ? <DueLabel due={s.due} overdue={s.overdue ?? false} /> : null}
                      {s.assignees?.length ? (
                        <AvatarStack
                          items={s.assignees.map((p) => ({
                            name: p.name,
                            kind: p.kind ?? "person",
                          }))}
                        />
                      ) : null}
                    </span>
                  ),
                };
              })}
              {...(taskId ? { selectedId: taskId } : {})}
              onOpen={onOpenTask}
              empty={bodyEmpty}
            />
          )}
        </div>

        {selected.length ? (
          <BulkBar
            api={api}
            company={company}
            board={board}
            boards={(boards.data?.data ?? []).filter((x) => x.id !== board.id)}
            people={people}
            ids={selected}
            tasks={flat}
            onDone={() => setSelected([])}
          />
        ) : null}
      </div>
      <NewViewDialog
        key={`${newView.open}-${newView.asCopy}`}
        open={newView.open}
        onOpenChange={(open) => setNewView((v) => ({ ...v, open }))}
        initialKind={kind}
        pending={createView.isPending}
        error={createView.error}
        onCreate={(name, k) =>
          createView.mutate({
            name,
            kind: k,
            settings: newView.asCopy ? settings : startingSettings(k),
          })
        }
      />
      <NewTaskDialog
        key={JSON.stringify(adding ?? {})}
        api={api}
        company={company}
        board={board}
        people={people}
        open={adding !== null}
        onOpenChange={(open) => (open ? null : setAdding(null))}
        defaults={adding ?? {}}
        onCreated={onOpenTask}
      />
    </>
  );
}

/** A new view's starting settings: boards group by status, everything else is ungrouped. */
function startingSettings(kind: View["kind"]): Settings {
  return kind === "board"
    ? FALLBACK
    : { filter: {}, sort: [{ field: "due_at", direction: "asc" }], group_by: null };
}

function FilterMenu({
  board,
  people,
  settings,
  onChange,
  className,
  extra,
}: {
  board: Board;
  people: SeatPeople;
  settings: Settings;
  onChange: (s: Partial<Settings>) => void;
  className?: string;
  /** WOS-BUNDLE: more groups after the built-in ones (the Tags group). */
  extra?: ReactNode;
}) {
  const f = settings.filter;
  const count = (f.all?.length ?? 0) + (f.any?.length ?? 0) + (f.include_done === false ? 1 : 0);
  const toggle = (field: string, value: unknown, on: boolean) => {
    const now = inValues(f, field);
    onChange({ filter: withIn(f, field, on ? [...now, value] : now.filter((x) => x !== value)) });
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <ToolbarButton
            icon={ListFilterIcon}
            label="Filter"
            active={count > 0}
            count={count}
            className={className}
          />
        }
      />
      <DropdownMenuContent align="end" className="max-h-[70vh] min-w-56 overflow-y-auto">
        <DropdownMenuGroup>
          <DropdownMenuCheckboxItem
            checked={f.include_done !== false}
            onCheckedChange={(on) => {
              const { include_done: _x, ...rest } = f;
              onChange({ filter: on ? rest : { ...rest, include_done: false } });
            }}
          >
            Show done tasks
          </DropdownMenuCheckboxItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuLabel>Status</DropdownMenuLabel>
          {board.status_set.map((s) => (
            <DropdownMenuCheckboxItem
              key={s.key}
              checked={inValues(f, "status").includes(s.key)}
              onCheckedChange={(on) => toggle("status", s.key, Boolean(on))}
            >
              {s.name}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuLabel>Priority</DropdownMenuLabel>
          {PRIORITIES.map((p) => (
            <DropdownMenuCheckboxItem
              key={p.value}
              checked={inValues(f, "priority").includes(p.value)}
              onCheckedChange={(on) => toggle("priority", p.value, Boolean(on))}
            >
              {p.label}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuLabel>Assignee or delegate</DropdownMenuLabel>
          {people.all
            .filter((p) => !p.closed)
            .map((p) => (
              <DropdownMenuCheckboxItem
                key={p.seatId}
                checked={inValues(f, "assignee").includes(p.seatId)}
                onCheckedChange={(on) => toggle("assignee", p.seatId, Boolean(on))}
              >
                {p.name}
                {p.de ? <span className="ml-auto text-xs text-muted-foreground">DE</span> : null}
              </DropdownMenuCheckboxItem>
            ))}
        </DropdownMenuGroup>
        {extra}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ActiveFilters({
  board,
  people,
  settings,
  onChange,
  tags,
}: {
  board: Board;
  people: SeatPeople;
  settings: Settings;
  onChange: (s: Partial<Settings>) => void;
  /** WOS-BUNDLE: names the tag filter's chip. */
  tags: ReadonlyMap<string, { name: string }>;
}) {
  const f = settings.filter;
  const label = (field: string, v: unknown) =>
    field === "status"
      ? (board.status_set.find((s) => s.key === v)?.name ?? String(v))
      : field === "priority"
        ? (PRIORITIES.find((p) => p.value === v)?.label ?? String(v))
        : field === "assignee"
          ? people.of(String(v)).label
          : String(v);
  const title = { status: "Status", priority: "Priority", assignee: "Assignee" } as Record<
    string,
    string
  >;
  const chips = (f.all ?? []).filter((c) => c.op === "in" && title[c.field]);
  const tagChips = (f.all ?? []).filter((c) => c.field === "tag");
  if (!chips.length && !tagChips.length && f.include_done !== false) return null;
  return (
    <FilterChips aria-label="Active filters" className="mt-1">
      {f.include_done === false ? (
        <FilterChip
          label="Hiding done tasks"
          removeLabel="Show done tasks"
          onRemove={() => {
            const { include_done: _x, ...rest } = f;
            onChange({ filter: rest });
          }}
        />
      ) : null}
      {chips.map((c) => (
        <FilterChip
          key={c.field}
          label={`${title[c.field]}: ${(c.value as unknown[]).map((v) => label(c.field, v)).join(", ")}`}
          removeLabel={`Remove the ${title[c.field]?.toLowerCase()} filter`}
          onRemove={() => onChange({ filter: withIn(f, c.field, []) })}
        />
      ))}
      {tagChips.map((c) => (
        <FilterChip
          key={`tag-${c.op}`}
          label={tagFilterLabel(tags, c) ?? "Tags"}
          removeLabel="Remove the tag filter"
          onRemove={() => {
            const all = (f.all ?? []).filter((x) => x !== c);
            const { all: _a, ...rest } = f;
            onChange({ filter: all.length ? { ...rest, all } : rest });
          }}
        />
      ))}
    </FilterChips>
  );
}

function SortMenu({
  settings,
  fields,
  onChange,
}: {
  settings: Settings;
  fields: FieldDef[];
  onChange: (s: Partial<Settings>) => void;
}) {
  const first = settings.sort[0];
  const options = [
    ...SORT_FIELDS,
    ...fields
      .filter((f) => f.type !== "relation")
      .map((f) => ({ value: `field:${f.id}`, label: f.name })),
  ];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={<ToolbarButton icon={ArrowUpDownIcon} label="Sort" active={Boolean(first)} />}
      />
      <DropdownMenuContent align="end" className="min-w-52">
        <DropdownMenuGroup>
          <DropdownMenuLabel>Sort by</DropdownMenuLabel>
          <DropdownMenuRadioGroup
            value={first?.field ?? ""}
            onValueChange={(v) =>
              onChange({
                sort: v ? [{ field: String(v), direction: first?.direction ?? "asc" }] : [],
              })
            }
          >
            <DropdownMenuRadioItem value="">Manual (creation order)</DropdownMenuRadioItem>
            {options.map((o) => (
              <DropdownMenuRadioItem key={o.value} value={o.value}>
                {o.label}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuGroup>
        {first ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuLabel>Direction</DropdownMenuLabel>
              <DropdownMenuRadioGroup
                value={first.direction}
                onValueChange={(v) =>
                  onChange({ sort: [{ field: first.field, direction: v as "asc" | "desc" }] })
                }
              >
                <DropdownMenuRadioItem value="asc">Ascending</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="desc">Descending</DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuGroup>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function GroupMenu({
  settings,
  fields,
  onChange,
}: {
  settings: Settings;
  fields: FieldDef[];
  onChange: (s: Partial<Settings>) => void;
}) {
  const options = [
    { value: "status", label: "Status" },
    { value: "priority", label: "Priority" },
    { value: "assignee", label: "Assignee" },
    ...fields
      .filter((f) => validGroupBy(`field:${f.id}`, { fields }))
      .map((f) => ({ value: `field:${f.id}`, label: f.name })),
  ];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <ToolbarButton icon={GroupIcon} label="Group" active={settings.group_by !== null} />
        }
      />
      <DropdownMenuContent align="end" className="min-w-48">
        <DropdownMenuGroup>
          <DropdownMenuLabel>Group by</DropdownMenuLabel>
          <DropdownMenuRadioGroup
            value={settings.group_by ?? ""}
            onValueChange={(v) => onChange({ group_by: v ? String(v) : null })}
          >
            <DropdownMenuRadioItem value="">No grouping</DropdownMenuRadioItem>
            {options.map((o) => (
              <DropdownMenuRadioItem key={o.value} value={o.value}>
                {o.label}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function TasksTable({
  board,
  fields,
  people,
  rows,
  today,
  selected,
  onSelected,
  taskId,
  onOpenTask,
  onStatus,
  onAssignee,
}: {
  board: Board;
  fields: FieldDef[];
  people: SeatPeople;
  rows: TaskCard[];
  today: string;
  selected: string[];
  onSelected: (ids: string[]) => void;
  taskId: string | undefined;
  onOpenTask: (id: string) => void;
  onStatus: (id: string, status: string) => void;
  onAssignee: (id: string, seatId: string, on: boolean) => void;
}) {
  const allOn = rows.length > 0 && rows.every((r) => selected.includes(r.id));
  const columns: TableViewColumn<TaskCard>[] = [
    {
      id: "select",
      header: "Select",
      accessor: () => null,
      sortable: false,
      className: "w-10",
      cell: (t) => (
        <Checkbox
          aria-label={`Select ${taskRef(t)} ${t.title}`}
          checked={selected.includes(t.id)}
          onClick={(e) => e.stopPropagation()}
          onCheckedChange={(on) =>
            onSelected(on ? [...selected, t.id] : selected.filter((x) => x !== t.id))
          }
        />
      ),
    },
    { id: "number", header: "#", accessor: (t) => t.number, numeric: true, className: "w-14" },
    { id: "title", header: "Title", accessor: (t) => t.title },
    {
      id: "status",
      header: "Status",
      accessor: (t) => board.status_set.findIndex((s) => s.key === t.status),
      cell: (t) => (
        <StatusMenu board={board} status={t.status} onChange={(s) => onStatus(t.id, s)} />
      ),
    },
    {
      id: "assignee",
      header: "Assignee",
      accessor: (t) => t.assignee_seat_ids.map((id) => people.of(id).label).join(", "),
      cell: (t) => (
        <AssigneeMenu
          people={people}
          seatIds={t.assignee_seat_ids}
          title={t.title}
          className="max-w-full opacity-100"
          onToggle={(seat, on) => onAssignee(t.id, seat, on)}
        />
      ),
    },
    {
      id: "due",
      header: "Due",
      accessor: (t) => t.due_at,
      cell: (t) =>
        t.due_at ? (
          <DueLabel
            due={localDay(t.due_at)}
            overdue={!isDone(t, board) && localDay(t.due_at) < today}
          />
        ) : null,
    },
    {
      id: "priority",
      header: "Priority",
      accessor: (t) => (t.priority === 0 ? 9 : t.priority),
      cell: (t) => {
        const p = priorityKey(t.priority);
        return p ? <PriorityMark priority={p} showLabel /> : null;
      },
    },
    ...fields
      // T3-TASK-ROLLUPS: rollups and formulas are computed on read, so they are columns too.
      .filter((f) => f.type !== "relation")
      .map(
        (f): TableViewColumn<TaskCard> => ({
          id: `field:${f.id}`,
          header: f.name,
          numeric:
            f.type === "number" ||
            f.type === "currency" ||
            f.type === "formula" ||
            f.type === "rollup",
          accessor: (t) => {
            const v = t.fields[f.id];
            if (v === null || v === undefined) return null;
            const choice = f.options.choices?.find((c) => c.key === v)?.name;
            if (choice) return choice;
            if (Array.isArray(v))
              return v
                .map((k) => f.options.choices?.find((c) => c.key === k)?.name ?? String(k))
                .join(", ");
            return typeof v === "number" ? v : String(v);
          },
        }),
      ),
  ];
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Checkbox
          aria-label="Select every task shown"
          checked={allOn}
          onCheckedChange={(on) => onSelected(on ? rows.map((r) => r.id) : [])}
        />
        Select all
      </div>
      <TableView
        label="Tasks"
        rows={rows}
        columns={columns}
        getRowId={(t) => t.id}
        onRowOpen={(t) => onOpenTask(t.id)}
        {...(taskId ? { selectedRowId: taskId } : {})}
        emptyTitle="No task matches"
        emptyDescription="Clear the search or the filters to see more."
      />
    </div>
  );
}

function addDays(day: string, n: number): string {
  const [y, m, d] = day.split("-").map(Number);
  return localDay(new Date(y ?? 1970, (m ?? 1) - 1, (d ?? 1) + n));
}

function TasksCalendar({
  api,
  company,
  board,
  rows,
  today,
  dateField,
  dateLabel,
  dateChoices,
  onDateField,
  chip,
  onOpenTask,
  onMove,
  onDayAdd,
}: {
  api: Api;
  company: CompanyAccess;
  board: Board;
  rows: TaskCard[];
  today: string;
  /** T4-VIEWS-MEDIA: the date the tasks sit on (`due_at`, `start_at`, `field:{uuid}`). */
  dateField: string;
  dateLabel: string;
  dateChoices: { ref: string; label: string }[];
  /** Saves the view's date (absent: the viewer may not edit the view). */
  onDateField?: (ref: string) => void;
  /** A chip's cover thumbnail and card fields. */
  chip: (t: TaskCard) => { thumb?: string; fields?: string[] };
  onOpenTask: (id: string) => void;
  onMove: (id: string, day: string) => void;
  onDayAdd: (day: string) => void;
}) {
  const [anchor, setAnchor] = useState(today);
  const [mode, setMode] = useState<"month" | "week">("month");
  const undated = rows.filter((t) => !taskDay(t, dateField)).length;
  // CALENDAR-C1 §3: the "Events" overlay, read-only chips that open the event in the Calendar.
  const [eventsOn, setEventsOn] = useBoardEventsOverlay(api, company.id);
  const navigate = useNavigate();
  const viewDays = daysOf(mode, anchor, 1);
  const win = windowOf(viewDays);
  const events = useQuery({
    ...occurrencesQuery(api, company.id, win.from, win.to),
    enabled: eventsOn,
    retry: false,
  });
  const eventItems = eventsOn
    ? (events.data?.data ?? []).flatMap((o) =>
        daysCovered(o, viewDays).map((day) => ({
          id: `event:${o.key}:${day}`,
          title: o.busy ? `Busy${o.owner_name ? ` · ${o.owner_name}` : ""}` : (o.title ?? ""),
          date: day,
          readOnly: true,
          ...(o.all_day ? {} : { time: clock(o.starts_at) }),
        })),
      )
    : [];
  const openItem = (id: string) => {
    if (!id.startsWith("event:")) return onOpenTask(id);
    const o = events.data?.data.find((x) => id.startsWith(`event:${x.key}:`));
    if (!o) return;
    void navigate({
      to: "/c/$slug/calendar",
      params: { slug: company.slug },
      search: {
        date: localDay(o.starts_at),
        ...(o.busy ? {} : { event: o.event_id }),
        ...(!o.busy && o.recurring ? { at: o.original_start_at ?? o.starts_at } : {}),
      },
    } as never);
  };
  return (
    <div className="flex flex-col gap-2">
      <CalendarView
        aria-label={`Tasks by ${dateLabel.toLowerCase()}`}
        items={[
          ...eventItems,
          ...rows.flatMap((t) => {
            const day = taskDay(t, dateField);
            return day
              ? [
                  {
                    id: t.id,
                    title: `${taskRef(t)} ${t.title}`,
                    date: day,
                    tone: statusTone(board.status_set.find((s) => s.key === t.status)),
                    done: isDone(t, board),
                    ...chip(t),
                  },
                ]
              : [];
          }),
        ]}
        anchor={anchor}
        today={today}
        mode={mode}
        onModeChange={setMode}
        onNavigate={setAnchor}
        onItemOpen={openItem}
        onItemMove={(id, day) => {
          if (!id.startsWith("event:")) onMove(id, day);
        }}
        onDayAdd={onDayAdd}
        actions={
          <span className="flex items-center gap-2 text-xs text-muted-foreground">
            {onDateField && dateChoices.length > 1 ? (
              <NativeSelect
                size="sm"
                aria-label="Calendar date"
                className="h-7 w-32"
                value={dateField}
                onChange={(e) => onDateField(e.target.value)}
              >
                {dateChoices.map((c) => (
                  <NativeSelectOption key={c.ref} value={c.ref}>
                    {c.label}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            ) : null}
            <Switch
              size="sm"
              aria-label="Show events"
              checked={eventsOn}
              onCheckedChange={(on) => setEventsOn(Boolean(on))}
            />
            <span aria-hidden="true">Events</span>
          </span>
        }
      />
      {undated ? (
        <p className="text-xs text-muted-foreground">
          {undated} task{undated === 1 ? " has" : "s have"} no {dateLabel.toLowerCase()} and{" "}
          {undated === 1 ? "is" : "are"} not on the calendar.
        </p>
      ) : null}
    </div>
  );
}

function TasksTimeline({
  board,
  rows,
  today,
  dateField,
  onOpenTask,
}: {
  board: Board;
  rows: TaskCard[];
  today: string;
  /** T4-VIEWS-MEDIA: `due_at` draws start → due; any other date draws a one-day bar on it. */
  dateField: string;
  onOpenTask: (id: string) => void;
}) {
  const span = (t: TaskCard): { start?: string; end?: string } => {
    if (dateField === "due_at")
      return {
        ...(t.start_at ? { start: localDay(t.start_at) } : {}),
        ...(t.due_at ? { end: localDay(t.due_at) } : {}),
      };
    const day = taskDay(t, dateField);
    return day ? { start: day, end: day } : {};
  };
  const days = rows
    .flatMap((t) => Object.values(span(t)))
    .filter((x): x is string => Boolean(x))
    .sort();
  const from = days[0] && days[0] < today ? addDays(days[0], -2) : addDays(today, -7);
  const last = days.at(-1);
  const to = last && last > addDays(today, 21) ? addDays(last, 3) : addDays(today, 28);
  // WORK-OS: the dependencies between the shown tasks, drawn as arrows.
  const shown = new Set(rows.map((t) => t.id));
  const dependencies = rows.flatMap((t) =>
    t.depends_on_task_ids.filter((d) => shown.has(d)).map((d) => ({ from: d, to: t.id })),
  );
  return (
    <TimelineView
      aria-label="Tasks on a timeline"
      dependencies={dependencies}
      items={rows.map((t) => ({
        id: t.id,
        title: `${taskRef(t)} ${t.title}`,
        ...span(t),
        tone: statusTone(board.status_set.find((s) => s.key === t.status)),
        done: isDone(t, board),
      }))}
      from={from}
      to={to}
      today={today}
      onItemOpen={onOpenTask}
      empty={<EmptyState variant="compact" icon={ChartGanttIcon} title="No task matches" />}
    />
  );
}

function BulkBar({
  api,
  company,
  board,
  boards,
  people,
  ids,
  tasks,
  onDone,
}: {
  api: Api;
  company: CompanyAccess;
  board: Board;
  boards: { id: string; name: string }[];
  people: SeatPeople;
  ids: string[];
  tasks: TaskCard[];
  onDone: () => void;
}) {
  const [confirm, setConfirm] = useState(false);
  type Action =
    | { kind: "status"; status: string }
    | { kind: "assign"; seat_ids: string[] }
    | { kind: "move"; board_id: string }
    | { kind: "delete" }
    | { kind: "tag"; tag_id: string; op: "add" | "remove" };
  // WORK-PERF: the selected rows change at once; the answer's refusals come back with the refetch.
  const writes = useTaskWrites(api, company);
  const bulk = {
    isPending: writes.bulk.isPending,
    mutate: (action: Action) => {
      writes.bulk.mutate({ ids, action }, { onSuccess: ({ results }) => report(results, action) });
    },
  };
  const report = (
    results: { task_id: string; ok: boolean; code: string | null }[],
    action: Action,
  ) => {
    const failed = results.filter((r) => !r.ok);
    const ok = results.length - failed.length;
    const verb =
      action.kind === "tag"
        ? action.op === "add"
          ? "tagged"
          : "untagged"
        : { status: "changed", assign: "assigned", move: "moved", delete: "deleted" }[action.kind];
    if (!failed.length)
      toast.add({ title: `${ok} task${ok === 1 ? "" : "s"} ${verb}`, type: "success" });
    else
      toast.add({
        title: `${ok} of ${results.length} ${verb}; ${failed.length} did not change`,
        description: failed
          .map((r) => {
            const t = tasks.find((x) => x.id === r.task_id);
            const why =
              r.code === "dependency_blocked"
                ? "waiting on a dependency"
                : r.code === "not_found"
                  ? "gone"
                  : (r.code ?? "refused");
            return `${t ? taskRef(t) : "A task"}: ${why}`;
          })
          .join(" · "),
        type: "warning",
      });
    onDone();
  };
  return (
    <section
      aria-label="Bulk actions"
      data-slot="bulk-bar"
      className="sticky bottom-4 z-20 mx-auto mt-4 flex w-fit max-w-full flex-wrap items-center gap-1 rounded-lg border border-border bg-popover px-2 py-1.5 text-sm shadow-lg"
    >
      <span className="px-2 font-medium tabular-nums">{ids.length} selected</span>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={<Button variant="ghost" size="sm" disabled={bulk.isPending} />}
        >
          Set status
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          {board.status_set.map((s) => (
            <DropdownMenuItem
              key={s.key}
              onClick={() => bulk.mutate({ kind: "status", status: s.key })}
            >
              {s.name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={<Button variant="ghost" size="sm" disabled={bulk.isPending} />}
        >
          <UserPlusIcon aria-hidden="true" />
          Assign
        </DropdownMenuTrigger>
        <DropdownMenuContent className="min-w-56">
          {people.all
            .filter((p) => !p.closed)
            .map((p) => (
              <DropdownMenuItem
                key={p.seatId}
                onClick={() => bulk.mutate({ kind: "assign", seat_ids: [p.seatId] })}
              >
                {p.name}
                <span className="ml-auto text-xs text-muted-foreground">
                  {p.de ? "delegate" : p.hint}
                </span>
              </DropdownMenuItem>
            ))}
        </DropdownMenuContent>
      </DropdownMenu>
      {boards.length ? (
        <DropdownMenu>
          <DropdownMenuTrigger
            render={<Button variant="ghost" size="sm" disabled={bulk.isPending} />}
          >
            <FolderInputIcon aria-hidden="true" />
            Move
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            {boards.map((x) => (
              <DropdownMenuItem
                key={x.id}
                onClick={() => bulk.mutate({ kind: "move", board_id: x.id })}
              >
                {x.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
      {/* WOS-BUNDLE: tag or untag the selection. */}
      <BulkTagMenu api={api} co={company.id} disabled={bulk.isPending} onAction={bulk.mutate} />
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <Button
          variant="ghost"
          size="sm"
          className="text-danger-text"
          disabled={bulk.isPending}
          onClick={() => setConfirm(true)}
        >
          <Trash2Icon aria-hidden="true" />
          Delete
        </Button>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {ids.length} task{ids.length === 1 ? "" : "s"}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              They go for everyone. Their subtasks stay, without a parent.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep them</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => bulk.mutate({ kind: "delete" })}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Button variant="ghost" size="icon-sm" aria-label="Clear the selection" onClick={onDone}>
        <XIcon aria-hidden="true" />
      </Button>
    </section>
  );
}
